'use client';
import React, { forwardRef, useEffect, useImperativeHandle, ReactNode } from 'react';
import ReactDOM from 'react-dom';
import { HeroBlock, MovieDetails } from '@intigral/shared';
import styles from './videopopup.module.sass';
import cn from 'classnames';
import type { MovieButtons } from '../MovieButtons';
import { MoviePreview } from '../MoviePreview';
import { useAnchoredPopup } from '../MoviePreview/useAnchoredPopup';
import { useMediaQuery } from '@intigral/hooks';

interface VideoPopupProps
    extends React.ComponentProps<typeof MovieButtons>,
        React.ComponentProps<typeof MovieDetails>,
        React.ComponentProps<typeof HeroBlock>,
        React.HTMLAttributes<HTMLDivElement> {
    cover: string;
    setIsActive?: React.Dispatch<React.SetStateAction<boolean>>;
    children?: React.ReactElement;
    hideMore?: boolean;
    closeSignal?: number;
}

export const VideoPopup = forwardRef(
    ({ cover, setIsActive, children, hideMore, closeSignal = 0, ...props }: VideoPopupProps, ref) => {
        const {
            play,
            progress,
            watchlist,
            watchlistButton,
            page,
            trailer,
            download,
            reminderButton,
            endActions,
            provider,
            locked,
            dolby,
            quality4K,
            onClickLock,
            rating,
            info,
            live,
            picture,
            heading,
            compact,
            minHeight,
            detail,
            theme,
            blurred,
            className,
            ...restDivProps
        } = props;

        const movieButtonsProps = {
            play,
            progress,
            watchlist,
            watchlistButton,
            page,
            trailer,
            download,
            reminderButton,
            endActions,
        };

        const movieDetailsProps = {
            provider,
            locked,
            dolby,
            quality4K,
            onClickLock,
            rating,
            info,
            live,
        };

        const heroBlockProps = {
            picture,
            heading,
            compact,
            minHeight,
            detail,
            theme,
            blurred,
        };

        const isMobile = useMediaQuery('(max-width: 924px)');

        const { cardRef, popupRef, showPopup, isOpen, setShowPopup, closePopup, handleMouseEnter, handleMouseLeave } =
            useAnchoredPopup({ enabled: !isMobile, onHoverChange: (next) => setIsActive?.(next) });

        useImperativeHandle(ref, () => ({
            showPopup: () => setShowPopup(true),
            hidePopup: () => closePopup(),
        }));

        useEffect(() => {
            if (!closeSignal) return;
            closePopup();
        }, [closeSignal]);

        const popupContent = (
            <MoviePreview
                ref={popupRef}
                className={cn(styles.popup, { [styles.fadeIn]: showPopup, [styles.fadeOut]: !showPopup }, className)}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                picture={heroBlockProps.picture ?? cover}
                heading={heroBlockProps.heading}
                play={movieButtonsProps.play}
                reminderButton={movieButtonsProps.reminderButton}
                watchlistButton={movieButtonsProps.watchlistButton}
                endActions={movieButtonsProps.endActions as ReactNode}
                // Desktop `···` opens the detail overlay; mobile keeps the bottom sheet below.
                onMore={page?.onClick}
                hideMore={hideMore}
                details={movieDetailsProps}
            />
        );

        return (
            <div
                ref={cardRef}
                className={className}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                {...(isMobile && page?.onClick
                    ? {
                          onClick: (event: React.MouseEvent<HTMLDivElement>) => {
                              const target = event.target as HTMLElement | null;
                              // 3-dot drawer / its modal must not open the detail overlay behind the sheet
                              if (
                                  target?.closest?.('[data-drawer-trigger="true"]') ||
                                  target?.closest?.('[data-drawer-modal="true"]')
                              ) {
                                  return;
                              }
                              page?.onClick?.(event as unknown as React.MouseEvent<HTMLButtonElement, MouseEvent>);
                          },
                      }
                    : {})}
                {...restDivProps}
            >
                {children}

                {!isMobile && isOpen && (ReactDOM.createPortal(popupContent, document.body) as ReactNode)}
            </div>
        );
    }
);
